/* eslint-disable react/jsx-no-target-blank */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { breakpoints, Heading, PageBlock, Stack, vars } from '@etg/wings';
import {
  DangerousHtmlContent,
  Icon as EtiIcon,
  Notification,
  NotificationPanel,
  variants,
} from '@eti/components';
import { useProperty, useTranslation } from '@eti/providers';
import { constants, string as stringUtil } from '@eti/utils';
import { css } from '@eti/styles';
import { FlightInformation, TripCharacteristics } from '@eti/types';
import { TravelerList } from '../../../widgets/traveler-list';
import TripDetailsContent from '../../../widgets/trip-details/components/TripDetailsContent';
import PriceDropProtectionNotification from '../../../common/components/PriceDropProtectionNotification';
import {
  VIRTUAL_INTERLINING,
  VIRTUAL_INTERLINING_WITH_SHORT_CONNECTION_TIME,
} from '../../../constants/tripCharacteristics';
import ReadMore from '../../../common/components/ReadMore';
import ExpandContent from './ExpandContent';

const blockStyles = css`
  border-top: 1px solid ${vars.colors.divider};
`;

const centeredContentStyles = css`
  align-items: center;
  display: flex;
  font-size: 0.875rem;
  justify-content: center;
  margin: 0.5rem 0 0.25rem;
`;

const eventSegmentListStyles = css`
  border-top: 1px solid ${vars.colors.divider};
  margin-top: 1rem;
  padding: 16px;
`;

const flightSectionStyles = css`
  background-color: inherit;
`;

const iconStyles = css`
  margin-right: 0.5rem;

  [dir='rtl'] & {
    margin-left: 0.5rem;
    margin-right: 0;
  }
`;

const removeProtocol = (url = '') => url.replace(/^(https?):\/\//, '');

const stackedIconStyles = css`
  height: 1.5rem;
  width: 1.5rem;

  @media (min-width: ${breakpoints._970}) {
    height: 2rem;
    width: 2rem;
  }
`;

const ReceiptTripDetails = ({
  baggageInfoUrl = '#',
  bounds,
  flightInformation = [],
  language,
  isMultiStop,
  segments,
  travelers,
  tripCharacteristics = [],
  checkedBaggageSummary,
}) => {
  const isVI = tripCharacteristics.includes(VIRTUAL_INTERLINING);
  const [flightInfoNotification, setFlightInfoNotification] = useState(
    flightInformation?.filter((element) => element.url !== null),
  );
  const { t } = useTranslation();
  const { p } = useProperty();

  const closeFlightInfoNotification = () => {
    setFlightInfoNotification(null);
  };

  const hasMoreThanOneSegment = () => {
    return bounds?.some((bound) => bound.segments.length > 1);
  };
  const showFlightInfoButton = hasMoreThanOneSegment() || tripCharacteristics.length > 0;

  return (
    <PageBlock
      aria-labelledby="receipt-tripdetails-heading"
      as="section"
      className={blockStyles}
      withPaddingY
    >
      <Stack spacing={16}>
        <Heading id="receipt-tripdetails-heading" level={2}>
          {t('Receipt.TripDetails.Heading')}
        </Heading>
        {p('PriceDropProtection.DisplayBanners.Enabled') && (
          <PriceDropProtectionNotification
            linkText={t('BookingFlow.PriceDropProtection.LinkText')}
            text={t('BookingFlow.PriceDropProtection.Text')}
            title={t('BookingFlow.PriceDropProtection.Title')}
            useContentWrapper={false}
          />
        )}
        {flightInfoNotification?.length > 0 && (
          <Notification
            closeButtonDataTestId="receipt-flight-info-notification-close-button"
            onClose={closeFlightInfoNotification}
            variant={variants.INFORMATION}
          >
            {flightInfoNotification.map(({ url, pnr, loginEmail }) =>
              loginEmail ? (
                <DangerousHtmlContent
                  content={stringUtil.insertArgument(
                    t('OrderPage.AirBooking.NotificationTemplate.LCC'),
                    removeProtocol(url),
                    pnr,
                    loginEmail,
                  )}
                  key={pnr}
                />
              ) : (
                <DangerousHtmlContent
                  content={stringUtil.insertArgument(
                    t('OrderPage.AirBooking.NotificationTemplate.GDS'),
                    removeProtocol(url),
                    pnr,
                  )}
                  key={pnr}
                />
              ),
            )}
          </Notification>
        )}
        {tripCharacteristics.includes(VIRTUAL_INTERLINING) && (
          <NotificationPanel
            dataTestId="orderPage-self-guarantee-info-TripNotification"
            icons={[
              { id: 'circle', size: '3rem', color: '#0c7e00' },
              { id: 'self-transfer', color: '#fff', className: stackedIconStyles },
            ]}
            title={t('SelfTransfer.InformationBox.Header.Text')}
            type={constants.notificationTypes.SELF_TRANSFER_GUARANTEE}
          >
            <ReadMore>
              <DangerousHtmlContent content={t('SelfTransfer.InformationBox.Content.Text')} />
            </ReadMore>
          </NotificationPanel>
        )}
        {tripCharacteristics.includes(VIRTUAL_INTERLINING_WITH_SHORT_CONNECTION_TIME) && (
          <Notification
            dataTestId="short-connection-time-notification"
            title={t('Order.NoBagFare.Header')}
            variant={variants.WARNING}
          >
            <DangerousHtmlContent content={t('Order.NoBagFare.Text')} />
          </Notification>
        )}
        <div className={flightSectionStyles}>
          {showFlightInfoButton ? (
            <ExpandContent
              buttonDataTestId="order-show-flight-info"
              label={t('Receipt.TripDetails.ShowFlightDetails')}
              labelHide={t('Receipt.TripDetails.HideFlightDetails')}
            >
              {({ isExpanded }) => (
                <TripDetailsContent
                  bounds={bounds}
                  eventSegmentListStyles={eventSegmentListStyles}
                  isExpanded={isExpanded}
                  isMultiStop={isMultiStop}
                  isVI={isVI}
                  language={language}
                  shouldShowBoundHeader
                  tripCharacteristics={tripCharacteristics}
                />
              )}
            </ExpandContent>
          ) : (
            <TripDetailsContent
              bounds={bounds}
              eventSegmentListStyles={eventSegmentListStyles}
              isExpanded
              isMultiStop={isMultiStop}
              isVI={isVI}
              language={language}
              shouldShowBoundHeader
              tripCharacteristics={tripCharacteristics}
            />
          )}
        </div>
        <ExpandContent
          buttonDataTestId="order-show-baggage-info"
          label={t('Receipt.TripDetails.ShowBaggage')}
          labelHide={t('Receipt.TripDetails.HideBaggage')}
        >
          {({ isExpanded }) => (
            <>
              <TravelerList
                checkedBaggageSummary={checkedBaggageSummary}
                isExpanded={isExpanded}
                segments={segments}
                travelers={travelers}
              />
              <div className={centeredContentStyles}>
                <a href={baggageInfoUrl} rel="noopener" target="_blank">
                  <EtiIcon
                    className={iconStyles}
                    color="currentColor"
                    id="off-page-link"
                    size="1rem"
                  />
                  <span>{t('Receipt.TripDetails.BaggageInfo')}</span>
                </a>
              </div>
            </>
          )}
        </ExpandContent>
      </Stack>
    </PageBlock>
  );
};

const baggageDetails = PropTypes.shape({
  __typename: PropTypes.string,
  baggageDetails: PropTypes.shape({
    __typename: PropTypes.string,
    pieces: PropTypes.any,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  baggageStatus: PropTypes.string,
  travelerId: PropTypes.string,
});

ReceiptTripDetails.propTypes = {
  baggageInfoUrl: PropTypes.string,
  bounds: PropTypes.arrayOf(
    PropTypes.shape({
      boundIndex: PropTypes.number,
      marketingMajorityCarrier: PropTypes.string,
      segments: PropTypes.arrayOf(PropTypes.object),
      travelTime: PropTypes.string,
    }),
  ),
  checkedBaggageSummary: PropTypes.shape({
    __typename: PropTypes.string,
    boundSummaries: PropTypes.arrayOf(
      PropTypes.shape({
        __typename: PropTypes.string,
        boundId: PropTypes.string,
        segmentSummaries: PropTypes.arrayOf(
          PropTypes.shape({
            __typename: PropTypes.string,
            segmentId: PropTypes.string,
            summaries: PropTypes.arrayOf(baggageDetails),
          }),
        ),
        summaries: PropTypes.arrayOf(baggageDetails),
      }),
    ),
    tripSummaries: PropTypes.arrayOf(baggageDetails),
  }),
  flightInformation: PropTypes.arrayOf(FlightInformation),
  isMultiStop: PropTypes.bool,
  language: PropTypes.string,
  segments: PropTypes.arrayOf(PropTypes.object),
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      birthDate: PropTypes.string,
      firstName: PropTypes.string,
      gender: PropTypes.string,
      id: PropTypes.string,
      lastName: PropTypes.string,
      name: PropTypes.string,
      passportNumber: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          quantity: PropTypes.number,
        }),
      ),
    }),
  ),
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
};

export default ReceiptTripDetails;
